import React, { useCallback, useMemo } from 'react'

import {
  AlignItems,
  BorderStyle,
  Box,
  Color,
  Display,
  FontWeight,
  Image,
  JustifyContent,
  Overflow,
  Space,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'

import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useMaxRecipesForPortion } from 'hooks/menu-boxes/useMaxRecipesForPortion'
import { useMinRecipesForPortion } from 'hooks/menu-boxes/useMinRecipesForPortion'
import type { RecipeUUID } from 'reducers/menu-recipes/recipes'
import { showDetailRecipe } from 'routes/Menu/actions/menuRecipeDetails'

import stackedRecipesImage from './assets/stacked-recipes.jpg'

type VisualBasketRecipe = {
  id: RecipeUUID
  imageUrl?: string
  key: string
  name: string
}

const ClickableRecipeTile = withCitrusTheme(Box, () => ({
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  padding: 0,
}))

const BasketPrompt = ({
  maxRecipesInBasket,
  minRecipesInBasket,
}: {
  maxRecipesInBasket: number
  minRecipesInBasket: number
}) => (
  <Box
    data-testid="visual-basket-prompt"
    alignItems={AlignItems.Center}
    borderColor={Color.ColdGrey_200}
    borderStyle={BorderStyle.Dashed}
    borderWidth={0.5}
    display={Display.Flex}
    flexGrow={[1, null, 0]}
    height="64px"
    paddingH={3}
    width="auto"
  >
    <Image
      data-testid="visual-basket-prompt-image"
      alt="recipe images stacked on top of each other"
      height="45px"
      src={stackedRecipesImage}
      width="48px"
    />
    <Space direction="horizontal" size={2} />
    <Box>
      <Text color={Color.White} display={Display.Block} fontWeight={FontWeight.Bold} size={2}>
        {`Add ${minRecipesInBasket} to ${maxRecipesInBasket} recipes you like`}
      </Text>
      <Text color={Color.White} display={Display.Block} size={2}>
        More recipes, better value.
      </Text>
    </Box>
  </Box>
)

const onKeyDown =
  (callback: () => void) =>
  (event: React.KeyboardEvent): void => {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      callback()
    }
  }

export const VisualBasket = () => {
  const dispatch = useDispatch()
  const { basketRecipes } = useBasketRecipes()
  const { numPortions } = useNumPortions()
  const maxRecipesInBasket = useMaxRecipesForPortion(numPortions)
  const minRecipesInBasket = useMinRecipesForPortion(numPortions)

  const selectedRecipes = useMemo<VisualBasketRecipe[]>(() => {
    const recipes: VisualBasketRecipe[] = []

    basketRecipes.forEach(({ recipe, quantity }) => {
      if (!recipe) {
        return
      }

      const imageCrops =
        recipe.images?.find(({ type }) => type === 'mood-image')?.crops || recipe.images?.[0]?.crops

      const imageUrl = imageCrops?.find(({ width }) => width === 200)?.url || imageCrops?.[0]?.url

      recipes.push(
        ...Array.from({ length: quantity }, (_, recipeCount) => ({
          id: recipe.id,
          imageUrl,
          key: `${recipe.id}-${recipeCount}`,
          name: recipe.name,
        })),
      )
    })

    return recipes.slice(0, maxRecipesInBasket)
  }, [basketRecipes, maxRecipesInBasket])

  const shouldShowEmptyPlaceholder = selectedRecipes.length === 0
  const shouldShowPrompt = selectedRecipes.length < minRecipesInBasket

  const openRecipeDetails = useCallback(
    (recipeId: RecipeUUID) => {
      dispatch(
        showDetailRecipe(recipeId, {
          section: 'visual_basket_tray',
        }),
      )
    },
    [dispatch],
  )

  return (
    <Box
      data-testid="visual-basket"
      alignItems={AlignItems.Center}
      display={Display.Flex}
      gap={2}
      justifyContent={JustifyContent.FlexStart}
      width={['100%', null, 'auto']}
    >
      {shouldShowEmptyPlaceholder && (
        <Box
          data-testid="visual-basket-placeholder-slot"
          bg={Color.ColdGrey_100}
          borderRadius={1}
          flexShrink={0}
          height="64px"
          minWidth="64px"
          overflowX={Overflow.Hidden}
          overflowY={Overflow.Hidden}
          width="64px"
        />
      )}

      {selectedRecipes.map((recipe) => {
        return (
          <Box
            data-testid="visual-basket-recipe-slot"
            key={recipe.key}
            borderRadius={1}
            flexShrink={0}
            height="64px"
            minWidth="64px"
            overflowX={Overflow.Hidden}
            overflowY={Overflow.Hidden}
            width="64px"
          >
            {recipe.imageUrl ? (
              <ClickableRecipeTile
                aria-label={`View ${recipe.name} details`}
                onClick={() => openRecipeDetails(recipe.id)}
                onKeyDown={onKeyDown(() => openRecipeDetails(recipe.id))}
                role="button"
                tabIndex={0}
                height="100%"
                width="100%"
              >
                <Image
                  data-testid="visual-basket-recipe-image"
                  alt={recipe.name}
                  fit="cover"
                  height="100%"
                  src={recipe.imageUrl}
                  width="100%"
                />
              </ClickableRecipeTile>
            ) : null}
          </Box>
        )
      })}

      {shouldShowPrompt && (
        <BasketPrompt
          maxRecipesInBasket={maxRecipesInBasket}
          minRecipesInBasket={minRecipesInBasket}
        />
      )}
    </Box>
  )
}
